import { AnyExtension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';

/**
 * THIS FILE IS THE HTML CONTRACT.
 *
 * Tiptap drops any node or mark that is not declared here — on paste, and
 * when loading existing content. Adding an extension widens what can end up
 * in the database and in customer PDFs, so treat changes here as a schema
 * change, not a UI tweak.
 *
 * Allowed: <p> <h1> <h2> <h3> <ul> <ol> <li> <strong> <em> <u> <a href> <br>
 *          plus class="ta-left|ta-center|ta-right" on block elements.
 *
 * Verified 2026-09-04: StarterKit 3.31.2 already bundles Link and Underline,
 * so neither is installed separately. Check
 * `node_modules/@tiptap/starter-kit/package.json` before adding either.
 */

/**
 * Alignment as a CLASS, not an inline style.
 *
 * Tiptap's stock TextAlign renders style="text-align:center". Angular's
 * [innerHTML] sanitizer strips style attributes, and the PDF is a
 * serialization of that already-sanitized DOM — so an inline style would
 * look right inside the editor and be lost everywhere the customer sees it.
 *
 * The matching `.ta-*` rules live in src/app/shared/styles/rich-text.scss.
 * Without that stylesheet these classes match nothing and alignment silently
 * does nothing — measured in Chrome 2026-09-04.
 */
export const OsmosTextAlign = TextAlign.extend({
  addGlobalAttributes() {
    const alignments: string[] = this.options.alignments ?? ['left', 'center', 'right'];
    const fallback: string | null = this.options.defaultAlignment ?? 'left';

    return [
      {
        types: this.options.types,
        attributes: {
          textAlign: {
            default: fallback,
            parseHTML: (element: HTMLElement) =>
              alignments.find((a) => element.classList.contains(`ta-${a}`)) ?? fallback,
            renderHTML: (attributes: Record<string, unknown>) => {
              const align = attributes['textAlign'];
              if (typeof align !== 'string' || align === fallback) {
                return {};
              }
              return { class: `ta-${align}` };
            },
          },
        },
      },
    ];
  },
});

/**
 * Link narrowed to `href` and nothing else.
 *
 * Two separate leaks had to be closed, both measured 2026-09-04:
 *
 * 1. Stock Link declares `target`, `rel` and `class` as real attributes, so
 *    they are parsed off incoming HTML and re-emitted. Clearing the option
 *    defaults does not stop this — an anchor pasted with rel="nofollow" came
 *    back carrying it. `addAttributes()` is replaced to declare href alone.
 * 2. Link's `renderHTML` merges `options.HTMLAttributes` into every anchor,
 *    which is where target="_blank" rel="noopener noreferrer nofollow" came
 *    from even after fix 1. `renderHTML` is replaced to emit href alone.
 *
 * Both are needed. The spec file proves it — if either is removed, the
 * "keeps links but strips target and rel" test fails.
 */
export const OsmosLink = Link.extend({
  addAttributes() {
    return {
      href: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('href'),
        renderHTML: (attributes: Record<string, unknown>) => {
          const href = attributes['href'];
          return typeof href === 'string' && href ? { href } : {};
        },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const href = (HTMLAttributes as Record<string, unknown>)['href'];
    return ['a', typeof href === 'string' && href ? { href } : {}, 0];
  },
});

export function osmosEditorExtensions(): AnyExtension[] {
  return [
    StarterKit.configure({
      // Contract allows h1-h3 only. h4-h6 are demoted to paragraphs.
      heading: { levels: [1, 2, 3] },

      // Replaced by OsmosLink below — see the note on that extension.
      link: false,

      // Out of contract — off.
      blockquote: false,
      code: false,
      codeBlock: false,
      horizontalRule: false,
      strike: false,

      // Bold, italic, underline, bulletList, orderedList, listItem, paragraph,
      // hardBreak and undo/redo stay at their defaults.
    }),

    OsmosLink.configure({ openOnClick: false }),

    OsmosTextAlign.configure({
      types: ['heading', 'paragraph'],
      alignments: ['left', 'center', 'right'],
      defaultAlignment: 'left',
    }),
  ] as AnyExtension[];
}
